/**
 * Sitter profile completion checklist — mirrors the family flow.
 * Verification badge only unlocks once these + admin approval are done.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Sparkles } from "lucide-react";

const CHECKS = [
  { key: "photo", label: "Add a professional photo", detail: "Families 5× more likely to hire sitters with photos", to: "/sitter", check: (p) => !!(p?.photo_url || p?.photo_path) },
  { key: "bio", label: "Write your bio", detail: "At least a few sentences about your experience & approach", to: "/sitter/profile", check: (p) => (p?.biography || "").length >= 40 },
  { key: "pitch", label: "One-line elevator pitch", detail: "The hook families see first", to: "/sitter/profile", check: (p) => (p?.elevator_pitch || "").length >= 20 },
  { key: "rate", label: "Set your hourly rate", detail: "Families filter by rate — set yours to appear in results", to: "/sitter/profile", check: (p) => (p?.hourly_rate || 0) > 0 },
  { key: "location", label: "City, state, ZIP", detail: "So we can match families in your area", to: "/sitter/profile", check: (p) => !!(p?.city && p?.state && p?.zip_code) },
  { key: "certs", label: "List at least one credential", detail: "RBT, CPR, teacher, etc.", to: "/sitter/profile", check: (p) => (p?.certifications?.length || 0) > 0 },
  { key: "respite", label: "Respite services you offer", detail: "Overnight, travel, community, party buffers", to: "/sitter/profile", check: (p) => (p?.respite_services?.length || 0) > 0 },
  { key: "connect", label: "Connect Stripe for payouts", detail: "Required before you can invoice families", to: "/sitter", check: (p) => !!p?.stripe_connect_charges_enabled },
  { key: "docs", label: "Upload verification documents", detail: "ID, background check, CPR, references", to: "/sitter", check: (p) => Object.keys(p?.verifications?.uploads || {}).length >= 4 },
];


export default function SitterProfileCompletion({ profile }) {
  const results = CHECKS.map((c) => ({ ...c, done: c.check(profile) }));
  const doneCount = results.filter((r) => r.done).length;
  const total = results.length;
  const pct = Math.round((doneCount / total) * 100);
  const nextItem = results.find((r) => !r.done);

  if (doneCount === total) {
    return (
      <div className="mt-6 rounded-xl border border-sage-300 bg-sage-50 p-4 flex items-center gap-3" data-testid="sitter-completion-done">
        <Sparkles className="w-5 h-5 text-sage-600" />
        <div>
          <div className="font-heading font-semibold text-sage-900">Your profile is complete 🎉</div>
          <p className="text-xs text-stone2-700">Admin will review your documents next — check back within 1–2 business days.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 paper-card p-5" data-testid="sitter-completion-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-bold text-sage-900 text-lg">Complete your sitter profile</div>
          <p className="text-sm text-stone2-600 mt-1">Families only see fully-completed profiles — finish these {total - doneCount} items to appear in search.</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-heading font-bold text-sage-900" data-testid="sitter-completion-pct">{pct}%</div>
          <div className="text-xs text-stone2-500">{doneCount} of {total} complete</div>
        </div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-stone2-100 overflow-hidden">
        <div className="h-full bg-sage-600 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {nextItem && (
        <Link to={nextItem.to} data-testid="sitter-completion-next-cta"
          className="mt-4 rounded-lg bg-ochre-50 border border-ochre-200 p-3 flex items-center gap-3 hover:bg-ochre-100">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-ochre-700">Next step</div>
            <div className="font-heading font-semibold text-sage-900">{nextItem.label}</div>
            <div className="text-xs text-stone2-600">{nextItem.detail}</div>
          </div>
          <ArrowRight className="w-5 h-5 text-ochre-700" />
        </Link>
      )}

      <ul className="mt-4 space-y-2">
        {results.map((r) => (
          <li key={r.key} data-testid={`sitter-check-${r.key}`}>
            <Link to={r.to}
              className={`flex items-center gap-3 p-2 rounded-lg text-sm transition-colors ${
                r.done ? "text-stone2-500" : "text-sage-900 hover:bg-sage-50"
              }`}>
              {r.done ? <CheckCircle2 className="w-5 h-5 text-sage-600 shrink-0" /> : <Circle className="w-5 h-5 text-stone2-300 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className={r.done ? "line-through" : "font-semibold"}>{r.label}</div>
                {!r.done && <div className="text-xs text-stone2-500 mt-0.5">{r.detail}</div>}
              </div>
              {!r.done && <ArrowRight className="w-4 h-4 text-stone2-400 shrink-0" />}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
